import type { Merchant } from "@unbox/api";
import { useMe } from "@/api/me";
import { useWorkspaceId } from "@/providers/MerchantProvider";

type WorkspaceSummary = {
  id: string;
  name: string;
  logoUrl: string | null;
  merchants: Merchant[];
};

type WorkspaceAdminState =
  | { isLoading: true; workspace: undefined; isWorkspaceAdmin: false }
  | { isLoading: false; workspace: WorkspaceSummary | undefined; isWorkspaceAdmin: boolean };

/**
 * Resolves the workspace for the `$workspaceId` in the current URL and whether the
 * current user is a Workspace Admin of it (UNB-1912 + UNB-1953). This is also the
 * sole access check for the workspace routes (UNB-2694) — `workspace` comes back
 * `undefined` the same way whether the id doesn't exist or the user just isn't a
 * member, so callers can't tell the two apart.
 */
export const useWorkspaceAdmin = (): WorkspaceAdminState => {
  const { data: me, isLoading } = useMe();
  const workspaceId = useWorkspaceId();

  if (isLoading) return { isLoading: true, workspace: undefined, isWorkspaceAdmin: false };

  const workspace = workspaceId ? me?.workspaces?.find((w) => w.id === workspaceId) : undefined;
  const isWorkspaceAdmin = workspace?.role === "workspace_admin";

  return { isLoading: false, workspace, isWorkspaceAdmin };
};
